import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  Target,
  Activity,
  Users,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Loader,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import './CoachNotesStatistics.css';

const CoachNotesStatistics = ({ onClose }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    priorities: true,
    jumps: false,
    phases: false,
    alignment: false,
    progress: false,
    insights: false
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/notes/statistics`);

      if (response.data.exists) {
        setStatistics(response.data);
      } else {
        setStatistics(null);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err.response?.data?.detail || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'worsening':
        return <TrendingUp size={16} className="trend-icon worsening" />;
      case 'improving':
        return <TrendingDown size={16} className="trend-icon improving" />;
      case 'stable':
        return <Minus size={16} className="trend-icon stable" />;
      case 'new':
        return <Sparkles size={16} className="trend-icon new" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority) => {
    if (priority >= 9) return 'critical';
    if (priority >= 7) return 'high';
    if (priority >= 5) return 'medium';
    return 'low';
  };

  const renderPriorityIssues = () => {
    if (!statistics?.parsed?.priority_issues || statistics.parsed.priority_issues.length === 0) {
      return <p className="no-data">No priority issues identified yet.</p>;
    }

    return (
      <div className="priority-issues-list">
        {statistics.parsed.priority_issues.map((issue, index) => (
          <div key={index} className="priority-issue-card">
            <div className="issue-header">
              <div className="issue-rank">{issue.rank}</div>
              <div className="issue-title-section">
                <h4 className="issue-name">{issue.name}</h4>
                <div className="issue-meta">
                  {getTrendIcon(issue.trend)}
                  <span className="frequency-badge">{issue.frequency} mentions</span>
                  {issue.jumps && issue.jumps.length > 0 && (
                    <div className="jump-tags">
                      {issue.jumps.slice(0, 3).map((jump, i) => (
                        <span key={i} className="jump-tag">{jump}</span>
                      ))}
                      {issue.jumps.length > 3 && (
                        <span className="jump-tag more">+{issue.jumps.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={`priority-score ${getPriorityColor(issue.priority)}`}>
                {issue.priority}/10
              </div>
            </div>

            {issue.cues && issue.cues.length > 0 && (
              <div className="issue-cues">
                <strong>Coaching Cues:</strong>
                <div className="cues-list">
                  {issue.cues.map((cue, i) => (
                    <span key={i} className="cue-badge">{cue}</span>
                  ))}
                </div>
              </div>
            )}

            {issue.focus && (
              <div className="issue-focus">
                <strong>Recommended Focus:</strong> {issue.focus}
              </div>
            )}

            {issue.impact && (
              <div className="issue-impact">
                <strong>Expected Impact:</strong> {issue.impact}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderJumpBreakdown = () => {
    if (!statistics?.parsed?.jump_analysis) {
      return <p className="no-data">No jump-specific data available.</p>;
    }

    const jumps = Object.entries(statistics.parsed.jump_analysis);
    if (jumps.length === 0) {
      return <p className="no-data">No jump-specific data available.</p>;
    }

    return (
      <div className="jump-grid">
        {jumps.map(([jumpName, data]) => (
          <div key={jumpName} className="jump-card">
            <div className="jump-header">
              <h4>{jumpName}</h4>
              <span className="issue-count-badge">{data.issue_count} issues</span>
            </div>
            {data.top_issues && data.top_issues.length > 0 && (
              <ul className="jump-issues-list">
                {data.top_issues.map((issue, i) => (
                  <li key={i}>
                    {issue.name} <span className="freq">({issue.frequency}x)</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderPhaseAnalysis = () => {
    if (!statistics?.parsed?.phase_analysis) {
      return <p className="no-data">No phase-specific data available.</p>;
    }

    const phases = Object.entries(statistics.parsed.phase_analysis);
    if (phases.length === 0) {
      return <p className="no-data">No phase-specific data available.</p>;
    }

    return (
      <div className="phase-grid">
        {phases.map(([phaseName, data]) => (
          <div key={phaseName} className="phase-card">
            <div className="phase-header">
              <h4>{phaseName} Phase</h4>
              <span className="issue-count-badge">{data.issue_count} issues</span>
            </div>
            {data.top_issues && data.top_issues.length > 0 && (
              <ul className="phase-issues-list">
                {data.top_issues.map((issue, i) => (
                  <li key={i}>
                    {issue.name} <span className="freq">({issue.frequency}x)</span>
                  </li>
                ))}
              </ul>
            )}
            {data.patterns && (
              <p className="phase-patterns">{data.patterns}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderBodyAlignment = () => {
    if (!statistics?.parsed?.body_alignment) {
      return <p className="no-data">No body alignment data available.</p>;
    }

    const bodyParts = Object.entries(statistics.parsed.body_alignment);
    if (bodyParts.length === 0) {
      return <p className="no-data">No body alignment data available.</p>;
    }

    return (
      <div className="alignment-list">
        {bodyParts.map(([partName, issues]) => (
          <div key={partName} className="alignment-section">
            <h4>{partName}</h4>
            <ul>
              {issues.map((issue, i) => (
                <li key={i}>
                  <strong>{issue.issue}:</strong> {issue.jumps} ({issue.frequency}x)
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const renderProgress = () => {
    if (!statistics?.parsed?.progress) {
      return <p className="no-data">No progress data available.</p>;
    }

    const { improving, persistent, new: newIssues, resolved } = statistics.parsed.progress;

    return (
      <div className="progress-sections">
        {improving && improving.length > 0 && (
          <div className="progress-section improving">
            <h4><TrendingDown size={18} /> Improving Areas</h4>
            <ul>
              {improving.map((item, i) => (
                <li key={i}>
                  <strong>{item.issue}:</strong> {item.evidence}
                </li>
              ))}
            </ul>
          </div>
        )}

        {persistent && persistent.length > 0 && (
          <div className="progress-section persistent">
            <h4><Minus size={18} /> Persistent Challenges</h4>
            <ul>
              {persistent.map((item, i) => (
                <li key={i}>
                  <strong>{item.issue}:</strong> {item.evidence}
                </li>
              ))}
            </ul>
          </div>
        )}

        {newIssues && newIssues.length > 0 && (
          <div className="progress-section new">
            <h4><Sparkles size={18} /> New Issues</h4>
            <ul>
              {newIssues.map((item, i) => (
                <li key={i}>
                  <strong>{item.issue}:</strong> First seen on {item.first_seen}
                </li>
              ))}
            </ul>
          </div>
        )}

        {resolved && resolved.length > 0 && (
          <div className="progress-section resolved">
            <h4>✅ Resolved Issues</h4>
            <ul>
              {resolved.map((item, i) => (
                <li key={i}>
                  <strong>{item.issue}:</strong> Last seen on {item.last_seen}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderInsights = () => {
    if (!statistics?.parsed?.insights) {
      return <p className="no-data">No coaching insights available.</p>;
    }

    const { frequent_cues, systemic_patterns, warm_up_focus } = statistics.parsed.insights;

    return (
      <div className="insights-sections">
        {frequent_cues && frequent_cues.length > 0 && (
          <div className="insight-section">
            <h4>Most Frequent Cues</h4>
            <ol>
              {frequent_cues.map((cue, i) => (
                <li key={i}>
                  <strong>"{cue.cue}"</strong> ({cue.frequency} mentions)
                  <br />
                  <span className="cue-context">Used for: {cue.contexts}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {systemic_patterns && systemic_patterns.length > 0 && (
          <div className="insight-section">
            <h4>Systemic Patterns</h4>
            <ul>
              {systemic_patterns.map((pattern, i) => (
                <li key={i}>
                  <strong>{pattern.type}:</strong> {pattern.description}
                </li>
              ))}
            </ul>
          </div>
        )}

        {warm_up_focus && warm_up_focus.length > 0 && (
          <div className="insight-section">
            <h4>Recommended Warm-Up Focus</h4>
            <ul>
              {warm_up_focus.map((focus, i) => (
                <li key={i}>{focus}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="statistics-panel">
        <div className="statistics-loading">
          <Loader className="spinner" size={32} />
          <p>Analyzing your coaching notes...</p>
          <p className="hint">This may take 3-5 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statistics-panel">
        <div className="statistics-error">
          <AlertCircle size={32} />
          <p>Failed to load statistics</p>
          <p className="error-detail">{error}</p>
          <button onClick={fetchStatistics} className="retry-button">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="statistics-panel">
        <div className="statistics-empty">
          <Activity size={48} />
          <h3>No Statistics Available Yet</h3>
          <p>Add coaching notes to generate statistics automatically.</p>
          <p className="hint">Statistics will appear after you create your first note.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="statistics-panel">
      <div className="statistics-header">
        <div className="header-content">
          <h2>📊 Training Statistics</h2>
          <div className="statistics-meta">
            <span>Last updated: {new Date(statistics.created_at).toLocaleDateString()}</span>
            <span>•</span>
            <span>{statistics.parsed?.metadata?.total_notes || 0} notes analyzed</span>
          </div>
        </div>
        <button onClick={onClose} className="close-button">
          ✕
        </button>
      </div>

      <div className="statistics-content">
        {/* Priority Issues Section */}
        <div className="statistics-section">
          <div
            className="section-header clickable"
            onClick={() => toggleSection('priorities')}
          >
            <div className="section-title">
              <Target size={20} />
              <h3>🔥 Top Priority Issues</h3>
            </div>
            {expandedSections.priorities ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {expandedSections.priorities && (
            <div className="section-content">
              {renderPriorityIssues()}
            </div>
          )}
        </div>

        {/* Jump-Specific Breakdown */}
        <div className="statistics-section">
          <div
            className="section-header clickable"
            onClick={() => toggleSection('jumps')}
          >
            <div className="section-title">
              <Activity size={20} />
              <h3>🎯 Jump-Specific Breakdown</h3>
            </div>
            {expandedSections.jumps ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {expandedSections.jumps && (
            <div className="section-content">
              {renderJumpBreakdown()}
            </div>
          )}
        </div>

        {/* Phase Analysis */}
        <div className="statistics-section">
          <div
            className="section-header clickable"
            onClick={() => toggleSection('phases')}
          >
            <div className="section-title">
              <Activity size={20} />
              <h3>📊 Phase Analysis</h3>
            </div>
            {expandedSections.phases ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {expandedSections.phases && (
            <div className="section-content">
              {renderPhaseAnalysis()}
            </div>
          )}
        </div>

        {/* Body Alignment Issues */}
        <div className="statistics-section">
          <div
            className="section-header clickable"
            onClick={() => toggleSection('alignment')}
          >
            <div className="section-title">
              <Users size={20} />
              <h3>💪 Body Alignment Issues</h3>
            </div>
            {expandedSections.alignment ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {expandedSections.alignment && (
            <div className="section-content">
              {renderBodyAlignment()}
            </div>
          )}
        </div>

        {/* Progress Tracking */}
        <div className="statistics-section">
          <div
            className="section-header clickable"
            onClick={() => toggleSection('progress')}
          >
            <div className="section-title">
              <TrendingUp size={20} />
              <h3>📈 Progress Tracking</h3>
            </div>
            {expandedSections.progress ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {expandedSections.progress && (
            <div className="section-content">
              {renderProgress()}
            </div>
          )}
        </div>

        {/* Coaching Insights */}
        <div className="statistics-section">
          <div
            className="section-header clickable"
            onClick={() => toggleSection('insights')}
          >
            <div className="section-title">
              <Lightbulb size={20} />
              <h3>💡 Coaching Insights</h3>
            </div>
            {expandedSections.insights ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          {expandedSections.insights && (
            <div className="section-content">
              {renderInsights()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachNotesStatistics;
